import { isXingshanDeep80MaxDevice } from "@eweadn-qiankun/shared-components";
import {
  normalizeHuafendaEquipmentForSubApp,
  resolveHfdKeyboardTypeFromEquipment,
  getTabScopedEquipmentData,
  isHuafendaMagneticAxisEquipment,
  isDk75FamilyKeyboardType,
  supportsEs68OnboardPresetCards as supportsEs68OnboardPresetCardsShared,
} from "@eweadn-qiankun/shared-utils";

export const getCurrentDevice = () => getTabScopedEquipmentData() ?? {};

export const getCurrentKeyboardType = () => {
  const device = getCurrentDevice() as Record<string, unknown>;
  if (String(device?.solutionRovider ?? "") === "huafenda") {
    const normalized = normalizeHuafendaEquipmentForSubApp(device);
    const kt = resolveHfdKeyboardTypeFromEquipment(normalized);
    if (kt) return kt;
  }
  if (String(device?.solutionRovider ?? "") === "rongyuan") {
    const kt = String(device?.keyboardType ?? "").trim();
    return kt || "rongyuan87";
  }
  return device?.keyboardType;
};

export type DeviceConfigLike = {
  solutionRovider?: string;
  productId?: number;
  vendorId?: number;
  keyboardType?: string;
  displayname?: string;
  productName?: string;
  identifier?: string;
  magneticAxis?: boolean;
};

/** 触发页「狂暴模式」展示判定（与子应用 CustomizeOperation 一致） */
export function resolveShouldShowFuriousMode(
  device?: DeviceConfigLike | null
): boolean {
  const d = device ?? (getCurrentDevice() as DeviceConfigLike);
  // 华奋达磁轴：常态 0.02 / 狂暴 0.01
  if (isHuafendaMagneticAxisEquipment(d)) {
    return true;
  }
  const keyboardType = d?.keyboardType;
  if (
    keyboardType === "87" ||
    keyboardType === "es87" ||
    keyboardType === "x75v3" ||
    keyboardType === "gamma75" ||
    isDk75FamilyKeyboardType(keyboardType) ||
    keyboardType === "68" ||
    keyboardType === "68EVO"
  ) {
    return true;
  }
  const productId = d?.productId;
  const vendorId = d?.vendorId;
  if (keyboardType === "deep80max") {
    if (d?.magneticAxis === true) return true;
    return isXingshanDeep80MaxDevice(productId, vendorId);
  }
  return (
    (productId === 24065 && vendorId === 7334) ||
    (productId === 7245 && vendorId === 7334) ||
    (productId === 7241 && vendorId === 7334) ||
    isXingshanDeep80MaxDevice(productId, vendorId)
  );
}

/** 星闪触发页狂暴模式步进 0.001mm（非 0.005mm）：Gamma75 Ultra/Pro/Pro Evo、DK63 V2 单/双灯 */
export function isXingshanTriggerFuriousStep0_001Mm(
  productId?: number,
  vendorId?: number
): boolean {
  if (Number(vendorId) !== 7334) return false;
  const pid = Number(productId);
  return (
    pid === 0x1c45 ||
    pid === 0x1c4e ||
    pid === 0x1c51 ||
    pid === 0x1c4d ||
    pid === 0x1c49
  );
}

function readDeviceConfig(config?: DeviceConfigLike): DeviceConfigLike {
  return (config ?? getCurrentDevice()) as DeviceConfigLike;
}

export const ES68_PID_VID_STORAGE_KEY = "es68PidVid";
/** getUSBModeStatus 写入 USB3 实机 pid/vid 后广播，供混轴 v3 列表刷新 */
export const ES68_PID_VID_READY_EVENT = "keyboard-management:es68PidVid:ready";

export type Es68PidVid = { es68Pid: number; es68Vid: number };

export function readEs68PidVidFromStorage(): Es68PidVid | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(ES68_PID_VID_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Es68PidVid;
    const es68Pid = Number(parsed?.es68Pid);
    const es68Vid = Number(parsed?.es68Vid);
    if (!Number.isFinite(es68Pid) || !Number.isFinite(es68Vid)) return null;
    return { es68Pid, es68Vid };
  } catch {
    return null;
  }
}

export function isEs68LiteDevice(config?: DeviceConfigLike): boolean {
  const c = readDeviceConfig(config);
  return String(c.displayname ?? "").trim() === "ES68 Lite";
}

/** ES68 族混轴 v3 走 USB3 实机 pid；ES68 Lite 按普通星闪设备，不在此列 */
function usesEs68AxisListUsb3Pid(config?: DeviceConfigLike): boolean {
  return supportsEs68PhysicalButtonTab(config) && !isEs68LiteDevice(config);
}

export function writeEs68PidVidToStorage(pid: unknown, vid: unknown): void {
  const es68Pid = Number(pid);
  const es68Vid = Number(vid);
  if (!Number.isFinite(es68Pid) || !Number.isFinite(es68Vid)) return;
  try {
    if (typeof window === "undefined") return;
    const prev = readEs68PidVidFromStorage();
    window.localStorage.setItem(
      ES68_PID_VID_STORAGE_KEY,
      JSON.stringify({ es68Pid, es68Vid })
    );
    if (prev?.es68Pid !== es68Pid || prev?.es68Vid !== es68Vid) {
      window.dispatchEvent(new CustomEvent(ES68_PID_VID_READY_EVENT));
    }
  } catch {
    /* ignore */
  }
}

/** ES68 在 16K/32K 下等待 getUSBModeStatus 写入实机 pid/vid */
export function waitForEs68PidVid(
  timeoutMs = 3000
): Promise<Es68PidVid | null> {
  const existing = readEs68PidVidFromStorage();
  if (existing) return Promise.resolve(existing);
  if (typeof window === "undefined" || !usesEs68AxisListUsb3Pid()) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const onReady = () => {
      clearTimeout(timer);
      window.removeEventListener(ES68_PID_VID_READY_EVENT, onReady);
      resolve(readEs68PidVidFromStorage());
    };
    const timer = window.setTimeout(() => {
      window.removeEventListener(ES68_PID_VID_READY_EVENT, onReady);
      resolve(readEs68PidVidFromStorage());
    }, timeoutMs);
    window.addEventListener(ES68_PID_VID_READY_EVENT, onReady);
  });
}

/**
 * getAxisListV3 的 board_id：十进制板 ID → 无符号 32 位 → 小写十六进制并补零至 8 位。
 * 须用 `>>> 0`，否则最高位为 1 时 JS 有符号右移会得到 "-7fffffff" 这类非法串。
 */
export function formatAxisListV3BoardId(boardId: unknown): string {
  const n = Number(boardId);
  if (!Number.isFinite(n)) return "00000000";
  return (n >>> 0).toString(16).padStart(8, "0");
}

/**
 * getAxisListV3 请求用 pid/vid：ES68 族在 USB3（16K/32K）下须用 getUSBModeStatus 实机值，
 * 配置表里的 8K pid/vid 会导致混轴列表为空。
 */
export function resolveAxisListV3PidVid(options?: {
  sdkVendorId?: number;
  sdkProductId?: number;
  config?: DeviceConfigLike;
}): { vid: string; pid: string } {
  const configurationNow = readDeviceConfig(options?.config);
  const toHex = (n: number, width: 4) =>
    (n >>> 0).toString(16).padStart(width, "0");

  const es68Stored = readEs68PidVidFromStorage();
  if (usesEs68AxisListUsb3Pid(configurationNow) && es68Stored) {
    return {
      vid: toHex(es68Stored.es68Vid, 4),
      pid: toHex(es68Stored.es68Pid, 4),
    };
  }

  if (
    configurationNow?.solutionRovider === "xingshan" &&
    typeof configurationNow.vendorId === "number" &&
    typeof configurationNow.productId === "number"
  ) {
    return {
      vid: toHex(configurationNow.vendorId, 4),
      pid: toHex(configurationNow.productId, 4),
    };
  }

  const sdkVid = Number(options?.sdkVendorId);
  const sdkPid = Number(options?.sdkProductId);
  if (Number.isFinite(sdkVid) && Number.isFinite(sdkPid)) {
    return { vid: toHex(sdkVid, 4), pid: toHex(sdkPid, 4) };
  }

  return { vid: "0000", pid: "0000" };
}

/** 改键页「自定义 / 物理键」Tab：ES68 / ES68 EVO / ES68 Lite（侧边实体键） */
export function supportsEs68PhysicalButtonTab(
  config?: DeviceConfigLike
): boolean {
  const c = readDeviceConfig(config);
  if (c?.solutionRovider !== "xingshan") return false;

  const displayname = String(c.displayname ?? "");
  const keyboardType = String(c.keyboardType ?? "");
  if (
    (keyboardType === "68" && displayname === "ES68") ||
    (keyboardType === "68" && displayname === "ES68 Lite") ||
    (keyboardType === "68" && displayname === "ES68 TIANYI") ||
    (keyboardType === "68" && displayname === "ES68 EVO (Full)") ||
    (keyboardType === "68EVO" && displayname === "ES68 EVO")
  ) {
    return true;
  }

  const productId = Number(c.productId);
  const vendorId = Number(c.vendorId);
  return (
    (productId === 7204 && vendorId === 7334) ||
    (productId === 65153 && vendorId === 6790) ||
    (productId === 7215 && vendorId === 7334) ||
    (productId === 7242 && vendorId === 7334) ||
    (productId === 7244 && vendorId === 7334) ||
    (productId === 7248 && vendorId === 7334)
  );
}

/** 板载 M1–M4 预设名 + es68Card 卡片：ES68 Dawn / ES68 Lite / ES68 EVO */
export function supportsEs68OnboardPresetCards(
  config?: DeviceConfigLike
): boolean {
  return supportsEs68OnboardPresetCardsShared(config);
}

/** 优先 equipmentData，兼容 app-hidGlobalStore */
export function readActiveDeviceConfig(): DeviceConfigLike {
  const fromEquipment = getCurrentDevice();
  if (
    fromEquipment?.solutionRovider ||
    fromEquipment?.vendorId != null ||
    fromEquipment?.displayname
  ) {
    return fromEquipment as DeviceConfigLike;
  }
  try {
    if (typeof window === "undefined") return {};
    const appHidGlobalStore = window.localStorage.getItem("app-hidGlobalStore");
    if (appHidGlobalStore) {
      const parsed = JSON.parse(appHidGlobalStore) as {
        state?: { configurationNow?: DeviceConfigLike };
      };
      return parsed?.state?.configurationNow ?? {};
    }
  } catch {
    /* ignore */
  }
  return {};
}

export function isXingshanDk68Family(config?: DeviceConfigLike): boolean {
  const c = readDeviceConfig(config);
  if (c?.solutionRovider !== "xingshan") return false;
  const productId = Number(c.productId);
  const vendorId = Number(c.vendorId);
  return (productId === 7178 || productId === 7180) && vendorId === 7334;
}

export function isXingshanEs68SliceFamily(config?: DeviceConfigLike): boolean {
  const c = readDeviceConfig(config);
  if (c?.solutionRovider !== "xingshan") return false;
  const productId = Number(c.productId);
  const vendorId = Number(c.vendorId);
  const productName = String(c.productName ?? c.displayname ?? "");
  if (vendorId === 7334 && /\bDK68\s*V2\b/i.test(productName)) {
    return true;
  }
  return (
    (productId === 7186 && vendorId === 7334) ||
    (productId === 7211 && vendorId === 7334) ||
    (productId === 7203 && vendorId === 7334) ||
    (productId === 7212 && vendorId === 7334) ||
    (productId === 24065 && vendorId === 7334) ||
    (productId === 7245 && vendorId === 7334) ||
    (productId === 7241 && vendorId === 7334) ||
    (productId === 7204 && vendorId === 7334) ||
    (productId === 65153 && vendorId === 6790) ||
    (productId === 7215 && vendorId === 7334) ||
    (productId === 7199 && vendorId === 7334) ||
    (productId === 7242 && vendorId === 7334) ||
    (productId === 7244 && vendorId === 7334) ||
    (productId === 7248 && vendorId === 7334) ||
    (productId === 7194 && vendorId === 7334) ||
    (productId === 7228 && vendorId === 7334)
  );
}

/** 触发/性能 API：UI 视觉行 → SDK/固件矩阵行（slice 首行占位型号为 uiRow + 1） */
export function resolvePerformanceSdkRow(uiRow: number): number {
  const row = Number(uiRow);
  if (!Number.isFinite(row)) return uiRow;
  if (isXingshanDk68Family()) return row;
  if (isXingshanEs68SliceFamily()) return row + 1;
  // huafenda68 等：矩阵第 0 行为空占位，视觉行需映射回固件行
  const kt = String(readDeviceConfig()?.keyboardType ?? "").trim();
  if (kt === "huafenda68" || kt === "tank68he") return row + 1;
  return row;
}

/**
 * 高级键 API 行号：与触发页类似，但更新已有高级键时（advancedKeyUpdate）可能不再 +1。
 */
export function resolveAdvancedKeySdkRow(
  row: number,
  options?: { isSelected?: boolean; skipOffsetWhenUpdating?: boolean }
): number {
  const r = Number(row);
  if (!Number.isFinite(r)) return row;
  if (isXingshanDk68Family()) return r;
  if (isXingshanEs68SliceFamily()) {
    if (options?.isSelected) return r + 1;
    if (options?.skipOffsetWhenUpdating) return r;
    return r + 1;
  }
  // tank68he：矩阵第 0 行为空占位；编辑时 selectData.row 已是固件行
  const kt = String(readDeviceConfig()?.keyboardType ?? "").trim();
  if (kt === "tank68he") {
    if (options?.skipOffsetWhenUpdating) return r;
    return r + 1;
  }
  return r;
}

export function isCurrentXingshanDeep80MaxDevice(): boolean {
  const config = getCurrentDevice() as DeviceConfigLike;
  return isXingshanDeep80MaxDevice(config?.productId, config?.vendorId);
}

/** 星闪在线固件：是否展示「检查更新 / 版本历史」 */
export function supportsXingshanFirmwareHistory(
  config?: DeviceConfigLike
): boolean {
  const c = readDeviceConfig(config);
  if (c?.solutionRovider !== "xingshan") return false;
  if (supportsEs68OnboardPresetCardsShared(c)) return true;
  const productId = Number(c.productId);
  const vendorId = Number(c.vendorId);
  return (
    (productId === 7178 && vendorId === 7334) ||
    isXingshanDeep80MaxDevice(productId, vendorId) ||
    (productId === 7211 && vendorId === 7334) ||
    (productId === 7203 && vendorId === 7334) ||
    (productId === 7212 && vendorId === 7334) ||
    (productId === 7204 && vendorId === 7334) ||
    (productId === 65153 && vendorId === 6790) ||
    (productId === 7215 && vendorId === 7334) ||
    (productId === 7199 && vendorId === 7334) ||
    (productId === 7194 && vendorId === 7334) ||
    (productId === 7228 && vendorId === 7334) ||
    (productId === 7223 && vendorId === 7334) ||
    (productId === 0x1c45 && vendorId === 7334) ||
    (productId === 0x1c4e && vendorId === 7334) ||
    (productId === 0x1c51 && vendorId === 7334) ||
    (productId === 7213 && vendorId === 7334) ||
    (productId === 7242 && vendorId === 7334) ||
    (productId === 7244 && vendorId === 7334) ||
    (productId === 7248 && vendorId === 7334) ||
    (productId === 7245 && vendorId === 7334) ||
    (productId === 7241 && vendorId === 7334)
  );
}
